/**
 * [C] Custom slideshow — a copy of `slideshow-component.js` adapted so that more
 * than one slide is visible: the active slide is centred and the neighbours peek
 * at the edges (Freshful style). Differences from the stock slideshow:
 *   - slide effect with `slidesPerView: 'auto'` + `centeredSlides` (the stock one
 *     uses a fade effect, which can only show one slide);
 *   - no infinite scroll (`loop: false`, `rewind: true`);
 *   - controls live BELOW the banner (looked up in the section, not inside the
 *     Swiper container, so they are not clipped);
 *   - while autoplaying, the active pagination dash fills over the slide delay;
 *   - the scroll-zoom clip-path effect is dropped (it fights with the peek).
 * Slide content, motion animations, video play/pause and a11y are preserved.
 */
if (!customElements.get('slideshow-component-c')) {
  customElements.define(
    'slideshow-component-c',
    class SlideshowComponentC extends HTMLElement {
      constructor() {
        super();

        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
      }

      connectedCallback() {
        if (!this.enableSlider || !this.section) return;
        this.initSlider();
        this.bindDesignMode();
      }

      disconnectedCallback() {
        if (this.sliderInstance) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
      }

      initSlider() {
        // Match the stock slideshow: autoplay runs whenever a delay is set
        // (it is not gated on prefers-reduced-motion).
        const autoplayDelay = parseInt(this.dataset.autoplay, 10);
        const wantsAutoplay = autoplayDelay > 0;
        const additionModules = wantsAutoplay ? [FoxTheme.Swiper.Autoplay] : [];

        this.sliderOptions = {
          slidesPerView: 'auto',
          // Mobile: the active slide is centred so a neighbour peeks on BOTH
          // sides (Freshful style). Desktop (>= 768px): centring is turned off so
          // the slideshow behaves like a product slider — the first slide sits
          // flush with the page container's left edge (aligned with the header /
          // section headings), the following slides peek/overflow on the right,
          // and the last slide stops flush with the container's right edge. With
          // centring off, Swiper already pins the first/last slides to the
          // container edges, so centeredSlidesBounds is not needed.
          centeredSlides: true,
          breakpoints: {
            768: {
              centeredSlides: false,
            },
          },
          spaceBetween: parseInt(this.dataset.gap, 10) || 0,
          speed: parseInt(this.dataset.speed, 10) || 500,
          loop: false,
          rewind: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          a11y: { enabled: true, slideRole: 'group' },
          on: {
            init: this.handleAfterInit.bind(this),
          },
        };

        // Arrows live beside the banner (in the stage) and dots live below, so
        // both are looked up at the section level rather than inside one wrapper.
        const prevEl = this.section.querySelector('.slideshow-c__arrow--prev');
        const nextEl = this.section.querySelector('.slideshow-c__arrow--next');
        this.paginationEl = this.section.querySelector('.swiper-pagination');

        if (prevEl && nextEl) {
          this.sliderOptions.navigation = { prevEl, nextEl };
        }
        if (this.paginationEl) {
          this.sliderOptions.pagination = { el: this.paginationEl, clickable: true };
        }

        if (wantsAutoplay) {
          this.sliderOptions.autoplay = {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: this.dataset.pauseOnHover === 'true',
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        const slider = this.sliderInstance.slider;
        slider.on('slideChangeTransitionEnd', () => this.handleSlideChange(slider));
        this.handleSlideChange(slider);

        // The active pagination dash fills over the autoplay delay entirely in
        // CSS (theme's `.swiper-controls--pagination-bullets` + the
        // `--swiper-autoplay-delay` variable set on the section), so there is no
        // per-frame JS here — that keeps it as smooth as the stock slideshow.

        // Accessibility: focusing something inside a partially-visible (peeking)
        // slide brings that slide to the centre so it is fully usable.
        FoxTheme.a11y.getFocusableElements(this).forEach((element) => {
          if (element.classList.contains('swiper-button') || element.classList.contains('btn')) return;
          element.addEventListener('focusin', () => {
            const slide = element.closest('.swiper-slide');
            if (slide) slider.slideTo(slider.slides.indexOf(slide));
          });
        });

        // Click behaviour (Freshful-style): a click on a peeking (non-active)
        // slide brings it to the centre; clicking the already-centred slide
        // follows its link, if it has one. Swiper suppresses the click after a
        // drag, so a swipe never navigates.
        slider.slides.forEach((slide) => {
          slide.addEventListener('click', (event) => {
            const index = slider.slides.indexOf(slide);
            if (index === -1) return;

            if (index !== slider.activeIndex) {
              // Peeking slide → centre it, don't follow any link.
              event.preventDefault();
              slider.slideTo(index);
              return;
            }

            // Active slide → let genuine buttons / links do their own thing,
            // otherwise follow the whole-slide link. We navigate from JS because
            // the link overlay sits beneath the (full-slide) text content, so a
            // plain click wouldn't reach the <a> on its own.
            if (event.target.closest('a, button')) return;
            const link = slide.querySelector('.slideshow-c__link');
            if (link && link.getAttribute('href')) window.location.href = link.href;
          });
        });
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transitioning the first time.
        if (this.paginationEl) {
          const activeBullet = this.paginationEl.querySelector('.swiper-pagination-bullet-active');
          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // reflow
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(slider) {
        const activeSlide = slider.slides[slider.activeIndex];

        slider.slides.forEach((slide) => {
          const isActive = slide === activeSlide;

          if (slide.dataset.type === 'video') {
            const videoElement = FoxTheme.utils.displayedMedia(slide.querySelectorAll('video-element'));
            if (videoElement) isActive ? videoElement.play() : videoElement.pause();
          }

          const motionEls = slide.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            if (isActive) {
              if (!FoxTheme.config.motionReduced) {
                setTimeout(() => motionEl && motionEl.refreshAnimation && motionEl.refreshAnimation());
              }
            } else if (motionEl.hasAttribute('data-text')) {
              motionEl.resetAnimation && motionEl.resetAnimation();
            }
          });
        });
        // NB: the controls keep the section's own colour scheme — they are not
        // recoloured per slide (they sit below the banner on the page, not over
        // the image), so they stay visible whatever scheme each slide uses.
      }

      bindDesignMode() {
        if (!window.Shopify || !Shopify.designMode || !this.sliderInstance) return;
        const slider = this.sliderInstance.slider;

        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.sectionId !== this.sectionId) return;
          const index = Number(event.target.dataset.index);
          if (!Number.isNaN(index)) slider.slideTo(index);
          slider.autoplay?.stop();
        });

        document.addEventListener('shopify:block:deselect', (event) => {
          if (event.detail.sectionId !== this.sectionId) return;
          slider.autoplay?.start();
        });
      }
    }
  );
}
